import React from 'react'
import classnames from 'classnames'
import { t } from 'src/translations'

import { DiningOptionBehavior, Selection } from 'src/apollo/onlineOrdering'
import { flattenModifiers } from 'src/public/components/default_template/online_ordering/order/OrderItemsUtil'
import { CartErrorType } from 'src/public/components/online_ordering/CartContext'
import { use3pdConfig } from 'src/public/components/online_ordering/FulfillmentContext'
import FormattedPrice from 'src/shared/components/common/price/FormattedPrice'
import { normalizeHtmlId } from 'src/shared/js/normalizeUtils'

import ErrorNotice from 'shared/components/common/error_notice'

import { WarningOutlineIcon } from '@toasttab/buffet-pui-icons'

import {
  CartSelections,
  CartSelection,
  CompletedOrderSelections,
  CompletedOrderSelection
} from 'public/components/online_ordering/types'

import ItemPrice from './ItemPrice'
import { roundPrice } from 'src/lib/js/hooks/useFormatPrice'
import { FeeLineItems } from './FeeWithTooltip'

export const ECOMM_ITEM_NAME = 'Toast E-Commerce Shipping Fee'

const getOrderItemLabelId = (
  selection: CompletedOrderSelection | CartSelection
) => {
  const anySelection = selection as Selection
  return `orderItemSelection-${normalizeHtmlId(anySelection.guid || '')}`
}

export const OrderItem = ({
  selection,
  itemActions,
  showShippingValidationError,
  itemTags
}: {
  selection: CompletedOrderSelection | CartSelection
  itemActions?: React.ReactElement
  showShippingValidationError?: boolean
  /** Optional tags (e.g. time-based rule tag) to display under the item name */
  itemTags?: React.ReactNode
}) => {
  const labelledById = getOrderItemLabelId(selection)

  if (!selection) {
    return null
  }

  const fractionalQuantity =
    selection.usesFractionalQuantity &&
    `${selection.fractionalQuantity.quantity.toFixed(
      2
    )} ${selection.fractionalQuantity.unitOfMeasure.toLowerCase()}`
  // Occasionally prices come back with floating-point weirdness, e.g. 42.449999999999996.
  // First round each price before performing the comparison.
  const isItemDiscounted =
    roundPrice(selection.price || 0) <
    roundPrice(selection.preDiscountPrice || 0)
  const discountedPrice =
    selection.price === 0 ? (
      'Free'
    ) : (
      <FormattedPrice value={selection.price || 0} />
    )
  const flattennedModifiers = selection.modifiers
    ? flattenModifiers(selection.modifiers)
    : []
  const hasQuantitiesOverOne = flattennedModifiers.some(
    (modifier) => modifier.quantity && modifier.quantity > 1
  )

  return (
    <div className='item' data-testid='orderItem'>
      <div className='itemTable'>
        <div className='row details' id={labelledById}>
          <div className='quantity itemQuantity'>{selection.quantity || 1}</div>
          <div className='cell'>
            <h3 className='name'>{selection.name}</h3>
            {selection.usesFractionalQuantity ? (
              <div className='fractional'>{fractionalQuantity}</div>
            ) : null}
            <div className='mods'>
              {selection.modifiers
                ? flattennedModifiers.map((modifier, index) => (
                    <div
                      style={{ paddingLeft: `${modifier.level * 8}px` }}
                      className='mod'
                      data-testid={`mod-${index}`}
                      key={`mod${index}`}
                    >
                      {hasQuantitiesOverOne && (
                        <span className='quantity'>
                          {modifier.quantity ?? 1}
                        </span>
                      )}
                      {modifier?.name}
                    </div>
                  ))
                : null}
            </div>
          </div>
          <div className='rightColumn'>
            <span
              data-testid='price'
              className={classnames('price', {
                discountedPrice: isItemDiscounted
              })}
            >
              <FormattedPrice value={selection.preDiscountPrice || 0} />
            </span>
            {isItemDiscounted && (
              <span className='price' data-testid='discounted-price'>
                {discountedPrice}
              </span>
            )}
          </div>
          {itemTags ? (
            <div className='orderItemTags' data-testid='order-item-tags'>
              {itemTags}
            </div>
          ) : null}
        </div>
        {Boolean(itemActions) && (
          <div className={classnames('row', 'details')} role='group'>
            <div className='emptyCell'>&nbsp;</div>
            <div className='actions'>{itemActions}</div>
          </div>
        )}
      </div>
      {showShippingValidationError && (
        <div className='validationError' role='alert'>
          {t('order-section.item-cannot-be-shipped')}
        </div>
      )}
    </div>
  )
}

type OrderPriceProps = {
  loading: boolean
  subtotal?: number | null
  discountsTotal?: number
  diningOptionBehavior?: string
  deliveryFee?: number | null
  // Struck through beside deliveryFee when set.
  originalDeliveryFee?: number
  gratuityServiceCharges: number
  processingServiceCharges: number
  nonDeliveryNonGratuityNonUbpServiceCharges: number
  tax: number
  tip?: number
  fundraisingAmount?: number | null
  giftCardAppliedAmount?: number
  surchargeAmount?: number
  surchargeTaxAmount?: number
  enableTooltip?: boolean
  shipping?: number
  shippingMessage?: string
}

export const OrderPrices = (props: OrderPriceProps) => {
  const { uses3pd } = use3pdConfig()
  const is3pdDelivery =
    uses3pd && props.diningOptionBehavior === DiningOptionBehavior.Delivery

  return (
    <div className='prices'>
      {props.shippingMessage ? (
        <ItemPrice
          loading={props.loading}
          title={t('order-items.shipping')}
          shippingMessage={props.shippingMessage}
          amount={0}
        />
      ) : props.shipping ? (
        <ItemPrice
          loading={props.loading}
          title={t('order-items.shipping')}
          amount={props.shipping}
        />
      ) : null}
      <ItemPrice
        loading={props.loading}
        title={t('order-items.subtotal')}
        amount={props.subtotal}
      />
      {props.discountsTotal ? (
        <ItemPrice
          loading={props.loading}
          title={t('order-items.discounts')}
          amount={props.discountsTotal}
          negative={true}
          enableTooltip={props.enableTooltip}
          className='discountPrice'
        />
      ) : null}
      <FeeLineItems
        loading={props.loading}
        diningOptionBehavior={props.diningOptionBehavior}
        deliveryFee={props.deliveryFee}
        originalDeliveryFee={props.originalDeliveryFee}
        nonDeliveryNonGratuityNonUbpServiceCharges={
          props.nonDeliveryNonGratuityNonUbpServiceCharges
        }
        gratuityServiceCharges={props.gratuityServiceCharges}
        processingServiceCharges={props.processingServiceCharges}
        tax={props.tax}
      />
      {props.tip ? (
        <ItemPrice
          loading={props.loading}
          title={
            is3pdDelivery ? t('order-items.driver-tip') : t('order-items.tip')
          }
          amount={props.tip}
        />
      ) : null}
      {props.surchargeAmount ? (
        <ItemPrice
          loading={props.loading}
          title={t('order-items.credit-card-surcharge')}
          amount={props.surchargeAmount}
        />
      ) : null}
      {props.surchargeTaxAmount ? (
        <ItemPrice
          loading={props.loading}
          title={t('order-items.surcharge-tax')}
          amount={props.surchargeTaxAmount}
        />
      ) : null}
      {props.fundraisingAmount ? (
        <ItemPrice
          loading={props.loading}
          title={t('order-items.support-and-give')}
          amount={props.fundraisingAmount}
        />
      ) : null}
      {props.giftCardAppliedAmount ? (
        <ItemPrice
          loading={props.loading}
          title={t('order-items.gift-card')}
          amount={props.giftCardAppliedAmount}
          negative={true}
        />
      ) : null}
    </div>
  )
}

interface ItemListProps {
  selections?: CompletedOrderSelections | CartSelections | null
  ItemComponent?: any
  itemProps?: any
}

const ItemList = ({ selections, ItemComponent, itemProps }: ItemListProps) => {
  return (
    <ul className='itemList'>
      {selections
        ?.filter((selection) => selection?.name !== ECOMM_ITEM_NAME)
        .map((selection, index) => {
          const labelledById = getOrderItemLabelId(selection)
          return (
            <li
              key={`${selection?.name}-${index}`}
              aria-labelledby={labelledById}
            >
              <ItemComponent selection={selection} {...itemProps} />
            </li>
          )
        })}
    </ul>
  )
}

type OrderItemsProps = {
  error?: CartErrorType | null
  selections?: CompletedOrderSelections | CartSelections | null
  hasItems?: boolean
  ItemComponent?: any
  itemProps?: any
  noItemsComponent?: React.ReactElement
}

export const OrderItems = (props: OrderItemsProps) => {
  const hasItems =
    'hasItems' in props ? props.hasItems : !!props.selections?.length
  const ItemComponent = props.ItemComponent || OrderItem

  return (
    <div className='items'>
      {props.error?.kind === 'CartError' ? (
        <div className='warningBanner' role='alert'>
          <WarningOutlineIcon size='xs' accessibility='decorative' />
          <span>{props.error.message}</span>
        </div>
      ) : props.error?.kind === 'CartOutOfStockError' ? (
        <ErrorNotice expireAfterSeconds={5}>{props.error.message}</ErrorNotice>
      ) : null}
      {hasItems ? (
        <ItemList
          selections={props.selections}
          ItemComponent={ItemComponent}
          itemProps={props.itemProps}
        ></ItemList>
      ) : (
        props.noItemsComponent
      )}
    </div>
  )
}

export default OrderItems
